// 環境変数の型定義と取得

export interface AppConfig {
  // 既存のBedrock Direct API設定（後方互換性のため残す）
  apiBaseUrl: string;
  chatEndpoint: string;
  
  // 新しいKnowledge Base API設定（メイン使用）
  knowledgeBaseApiUrl: string;
  knowledgeBaseEndpoint: string;
  
  // 非同期Knowledge Base API設定（新規）
  asyncKnowledgeBaseEndpoint: string;
  asyncStatusEndpoint: string;
  
  // アプリケーション設定
  appName: string;
  debugMode: boolean;
  useMockApi: boolean;
  mockDelayMs: number;
  
  // Knowledge Base固有設定
  defaultMaxResults: number;
  
  // 非同期処理設定（新規）
  defaultPollInterval: number;
  maxPollAttempts: number;
  
  // API認証設定
  apiKey: string;
}

// 環境変数を取得する関数（型安全）
const getEnvVar = (key: string, defaultValue?: string): string => {
  const value = process.env[key];
  if (value === undefined) {
    if (defaultValue !== undefined) {
      return defaultValue;
    }
    throw new Error(`環境変数 ${key} が設定されていません`);
  }
  return value;
};

// 環境変数をboolean型に変換
const getBooleanEnv = (key: string, defaultValue: boolean = false): boolean => {
  const value = process.env[key];
  if (value === undefined) return defaultValue;
  return value.toLowerCase() === 'true';
};

// 環境変数をnumber型に変換
const getNumberEnv = (key: string, defaultValue: number): number => {
  const value = process.env[key];
  if (value === undefined) return defaultValue;
  const parsed = parseInt(value, 10);
  if (isNaN(parsed)) return defaultValue;
  return parsed;
};

// アプリケーション設定を取得
export const getAppConfig = (): AppConfig => {
  return {
    // 既存のBedrock Direct API設定（後方互換性）
    apiBaseUrl: getEnvVar('REACT_APP_API_BASE_URL', 'http://localhost:3001/api'),
    chatEndpoint: getEnvVar('REACT_APP_CHAT_ENDPOINT', '/chat'),
    
    // 新しいKnowledge Base API設定
    // 開発環境では空文字列（相対パス）、本番環境では絶対URL
    knowledgeBaseApiUrl: getEnvVar(
      'REACT_APP_KNOWLEDGE_BASE_API_URL', 
      'https://panda5.rag-application-study-panda.com'
    ),
    knowledgeBaseEndpoint: getEnvVar('REACT_APP_KNOWLEDGE_BASE_ENDPOINT', '/api/knowledge-base'),
    
    // 非同期Knowledge Base API設定（新規）
    asyncKnowledgeBaseEndpoint: getEnvVar('REACT_APP_ASYNC_KNOWLEDGE_BASE_ENDPOINT', '/api/knowledge-base-async'),
    asyncStatusEndpoint: getEnvVar('REACT_APP_ASYNC_STATUS_ENDPOINT', '/api/knowledge-base-status'),
    
    // アプリケーション設定
    appName: getEnvVar('REACT_APP_APP_NAME', 'RAG Chat Application'),
    debugMode: getBooleanEnv('REACT_APP_DEBUG_MODE', false),
    useMockApi: getBooleanEnv('REACT_APP_USE_MOCK_API', true),
    mockDelayMs: getNumberEnv('REACT_APP_MOCK_DELAY_MS', 1000),
    
    // Knowledge Base固有設定
    defaultMaxResults: getNumberEnv('REACT_APP_DEFAULT_MAX_RESULTS', 5),
    
    // 非同期処理設定（新規）
    defaultPollInterval: getNumberEnv('REACT_APP_DEFAULT_POLL_INTERVAL', 2000), // 2秒間隔
    maxPollAttempts: getNumberEnv('REACT_APP_MAX_POLL_ATTEMPTS', 150), // 5分間（2秒×150回）
    
    // API認証設定
    apiKey: getEnvVar('REACT_APP_API_KEY', '')
  };
};

// 設定のインスタンス（シングルトン）
export const appConfig = getAppConfig();
